import type { NotLicensedControls, ScanFreshness } from '../../lib/api'

/*
 * NOT LICENSED, NOT ASSESSED (migration 0158, owner 2026-09-26).
 *
 * A tenant licensed only for Entra ID P2 was scanned; Microsoft refused the Exchange, SharePoint and
 * Defender reads, and the product told the customer "24 controls could not be read -- a permission
 * may have changed" and marked Email and Data failed. The truth was that the tenant never bought
 * those services. The server now records such controls as not licensed -- ONLY when Microsoft
 * refused the read AND the tenant's licences are absent for that service -- and this file is the one
 * place the pages learn to say so.
 *
 * The rule every screen follows: a not-licensed area is NOT ASSESSED. It is never a clean zero,
 * never "No open findings in Email", and never counted as passing. A complete scan over an Email
 * area nobody could read looks exactly like a clean Email unless the page says otherwise, and that
 * is the false all-clear this product exists to refuse.
 *
 * One module rather than a helper per page, so the dashboard's rows, its card, the freshness line,
 * the area pages and the printed report cannot disagree about which areas were not assessed.
 */

/** Every group of controls this scan did not assess for want of a licence. Empty from an older server. */
export function notLicensedOf(freshness: ScanFreshness): NotLicensedControls[] {
  return freshness.notLicensed ?? []
}

/** The groups in one area (one per service), in the server's order. */
export function notLicensedIn(freshness: ScanFreshness, area: string): NotLicensedControls[] {
  return notLicensedOf(freshness).filter((n) => n.area === area)
}

/**
 * Whether an area was not assessed AT ALL, because nothing in it is licensed.
 *
 * Either signal is enough: the area's own flag, or a group saying it was the whole area. The server
 * derives both from the same column, so they agree; if they ever did not, reading either lands on
 * "not assessed" -- the direction that cannot be mistaken for clean.
 */
export function wholeAreaNotLicensed(freshness: ScanFreshness, area: string): boolean {
  return (
    freshness.areas.some((a) => a.area === area && a.notLicensed === true) ||
    notLicensedIn(freshness, area).some((n) => n.wholeArea)
  )
}

/** Every area not assessed at all for want of a licence, in a stable order. */
export function notLicensedAreas(freshness: ScanFreshness): string[] {
  const names = new Set<string>()
  for (const a of freshness.areas) if (a.notLicensed === true) names.add(a.area)
  for (const n of notLicensedOf(freshness)) if (n.wholeArea) names.add(n.area)
  return [...names].sort((a, b) => a.localeCompare(b))
}

/**
 * How many areas were not assessed at all. The server's own count where it is larger than the
 * names this page can see -- a count that shrinks on the way to the screen is the flattering
 * direction.
 */
export function notLicensedAreaCount(freshness: ScanFreshness): number {
  return Math.max(freshness.areasNotLicensed ?? 0, notLicensedAreas(freshness).length)
}

/**
 * Areas assessed only in part: some controls not licensed, the others assessed as usual (Endpoint's
 * Defender controls beside its Intune one). Never an area that was not assessed at all.
 */
export function partlyNotLicensedAreas(freshness: ScanFreshness): string[] {
  const whole = new Set(notLicensedAreas(freshness))
  return [...new Set(notLicensedOf(freshness).map((n) => n.area))]
    .filter((area) => !whole.has(area))
    .sort((a, b) => a.localeCompare(b))
}

/** The distinct service names in some groups, in order. */
export function serviceNames(groups: NotLicensedControls[]): string[] {
  return [...new Set(groups.map((g) => g.serviceName).filter((name) => name.trim().length > 0))]
}

/** How many controls some groups hold. */
export function controlCount(groups: NotLicensedControls[]): number {
  return groups.reduce((sum, g) => sum + g.controls, 0)
}

/**
 * "no Exchange Online licence", or "no licence for Exchange Online or SharePoint and OneDrive" when
 * an area ever reads more than one service.
 *
 * With no service named at all -- the area's flag arrived without its groups -- it says so in
 * general words rather than guessing a name: "no licence for the Microsoft service it reads".
 */
export function noLicenceFor(groups: NotLicensedControls[]): string {
  const names = serviceNames(groups)
  if (names.length === 0) return 'no licence for the Microsoft service it reads'
  if (names.length === 1) return `no ${names[0]} licence`
  return `no licence for ${listOf(names, 'or')}`
}

/** "Email", "Email and Data", "Data, Email and Endpoint". */
export function listOf(names: string[], joiner: 'and' | 'or' = 'and'): string {
  if (names.length <= 1) return names.join('')
  return `${names.slice(0, -1).join(', ')} ${joiner} ${names[names.length - 1]}`
}

/** "1 control" / "19 controls". */
export function controls(n: number): string {
  return `${n.toLocaleString()} ${n === 1 ? 'control' : 'controls'}`
}

/**
 * One sentence naming the areas not assessed at all, for a page-level "nothing open" line:
 * "Email and Data were not assessed, because this tenant has no licence for the Microsoft services
 * they read." Empty when there are none.
 */
export function notAssessedAreasSentence(freshness: ScanFreshness): string {
  const areas = notLicensedAreas(freshness)
  if (areas.length === 0) return ''
  return areas.length === 1
    ? `${areas[0]} was not assessed, because this tenant has no licence for the Microsoft service it reads.`
    : `${listOf(areas)} were not assessed, because this tenant has no licence for the Microsoft services they read.`
}

/**
 * The printed report's sentence, in the same words as the server's CSV preamble
 * (FindingEndpoints.NotLicensedSentence), so the two documents an auditor keeps say one thing:
 * "Not assessed, because this tenant has no licence for the Microsoft service they read and
 * Microsoft refused the read: Data (1 control, no SharePoint and OneDrive licence); Email (19
 * controls, no Exchange Online licence). These are not counted as passing." Empty when none.
 */
export function notLicensedReportSentence(freshness: ScanFreshness): string {
  const byArea = new Map<string, NotLicensedControls[]>()
  for (const n of notLicensedOf(freshness)) {
    const group = byArea.get(n.area)
    if (group) group.push(n)
    else byArea.set(n.area, [n])
  }
  if (byArea.size === 0) return ''

  const parts = [...byArea.entries()].map(([area, groups]) => {
    const names = serviceNames(groups).join(' and ')
    return `${area} (${controls(controlCount(groups))}, no ${names} licence)`
  })

  return 'Not assessed, because this tenant has no licence for the Microsoft service they read and '
    + `Microsoft refused the read: ${parts.join('; ')}. These are not counted as passing.`
}
